import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ListPlus, Search, Plus, Trash2, X, Save, Loader2, Dumbbell, ChevronLeft, Sparkles, Timer, History, PlayCircle } from 'lucide-react';
import { Card, Btn, Chip, Empty, LabeledInput } from './shared';
import SafetyAdaptedPanel from './SafetyAdaptedPanel';
import { api } from '../../lib/api';
import { youtubeVideoUrl, hasCuratedVideo } from '../../lib/exerciseVideos';
import { useToast } from '../../hooks/use-toast';

const AIGenerator = ({ onClose, onGenerated }) => {
  const [prompt, setPrompt] = useState('');
  const [duration, setDuration] = useState(45);
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  const generate = async () => {
    if (!prompt.trim()) return;
    setBusy(true);
    try {
      const r = await api.aiRoutine(prompt, Number(duration) || 45);
      const swaps = Array.isArray(r.avoided) ? r.avoided.filter((s) => typeof s === 'string' && s.includes('→')).length : 0;
      toast({
        title: 'Routine generated',
        description: swaps > 0
          ? `${r.exercises.length} exercises · ${swaps} swapped for your injuries`
          : `${r.exercises.length} exercises`,
      });
      onGenerated(r);
    } catch (err) {
      toast({ title: 'Failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="max-w-lg w-full bg-[#0f1115] border border-[#2a2f3a] rounded-2xl p-5 lg:p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-white font-extrabold text-xl flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#ff7a1a]" /> AI routine generator
          </h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-[#0b0c10] border border-[#2a2f3a] text-white flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-[#aab1bf] text-sm mt-2">Describe what you want — equipment and injuries are already taken into account.</p>
        <label className="flex flex-col gap-1.5 text-sm mt-4">
          <span className="text-[#cbd5e1] font-medium">What do you want today?</span>
          <textarea
            rows={3} value={prompt} onChange={(e) => setPrompt(e.target.value)}
            placeholder="e.g. 45-min upper body strength session, focus on chest and triceps"
            className="bg-[#0b0c10] border border-[#2a2f3a] focus:border-[#ff7a1a] focus:ring-2 focus:ring-[#ff7a1a]/20 rounded-lg px-3 py-2.5 text-white outline-none text-sm"
          />
        </label>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <LabeledInput label="Duration (min)" type="number" min="15" max="120" value={duration} onChange={(e) => setDuration(e.target.value)} />
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn onClick={generate} disabled={busy || !prompt.trim()}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {busy ? 'Generating…' : 'Generate'}
          </Btn>
        </div>
      </div>
    </div>
  );
};

const RestTimer = ({ defaultSeconds = 90 }) => {
  const [seconds, setSeconds] = useState(0);
  const [target, setTarget] = useState(defaultSeconds);
  const [running, setRunning] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (running) {
      ref.current = setInterval(() => setSeconds((s) => s + 1), 1000);
      return () => clearInterval(ref.current);
    }
  }, [running]);

  const start = () => { setSeconds(0); setRunning(true); };
  const stop = () => { setRunning(false); setSeconds(0); };

  const remaining = Math.max(0, target - seconds);
  const mm = String(Math.floor(remaining / 60)).padStart(2, '0');
  const ss = String(remaining % 60).padStart(2, '0');
  const done = running && remaining === 0;

  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${done ? 'border-emerald-500 bg-emerald-500/10' : 'border-[#2a2f3a] bg-[#0b0c10]'}`}>
      <Timer className={`w-4 h-4 ${done ? 'text-emerald-400' : 'text-[#ff7a1a]'}`} />
      <span className={`font-mono font-bold text-sm ${done ? 'text-emerald-400' : 'text-white'}`}>
        {done ? 'Go!' : `${mm}:${ss}`}
      </span>
      <select value={target} onChange={(e) => setTarget(Number(e.target.value))} disabled={running}
        className="bg-[#0f1115] border border-[#2a2f3a] rounded text-white text-xs px-1.5 py-0.5 outline-none ml-auto">
        <option value="60">60s</option><option value="90">90s</option>
        <option value="120">2m</option><option value="180">3m</option><option value="300">5m</option>
      </select>
      {!running ? (
        <button onClick={start} className="text-[#ff7a1a] font-bold text-xs uppercase tracking-wider">Start</button>
      ) : (
        <button onClick={stop} className="text-[#aab1bf] hover:text-white text-xs">Stop</button>
      )}
    </div>
  );
};

const ExerciseHistory = ({ exerciseName }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.listLifts(exerciseName).then((d) => setItems(d || [])).catch(() => {});
  }, [open, exerciseName]);

  return (
    <div className="mt-2">
      <button onClick={() => setOpen(!open)} className="text-[#7c8597] hover:text-[#ff7a1a] text-[11px] font-bold uppercase tracking-wider flex items-center gap-1">
        <History className="w-3 h-3" /> {open ? 'Hide history' : 'Show history'}
      </button>
      {open && (
        items.length === 0 ? (
          <p className="text-[#7c8597] italic text-xs mt-2">No sets logged yet for this exercise.</p>
        ) : (
          <ul className="mt-2 space-y-1 max-h-32 overflow-y-auto pr-1">
            {items.slice(0, 12).map((h) => (
              <li key={h.id} className="text-xs flex justify-between items-center bg-[#0f1115] border border-[#1f2937] rounded px-2 py-1">
                <span className="text-white font-semibold">{h.weight_kg}kg × {h.reps} × {h.sets}</span>
                <span className="text-[#7c8597]">RIR {h.rir} · {new Date(h.created_at).toLocaleDateString()}</span>
              </li>
            ))}
          </ul>
        )
      )}
    </div>
  );
};

const ExercisePicker = ({ onAdd, selectedIds }) => {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [equipment, setEquipment] = useState('');
  const [facets, setFacets] = useState({ categories: [], equipment: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.exerciseFacets().then(setFacets).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (category) params.category = category;
    if (equipment) params.equipment = equipment;
    api.exercises(params)
      .then((d) => setItems((d || []).slice(0, 60)))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [search, category, equipment]);

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="w-4 h-4 text-[#7c8597] absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search exercises…"
          className="w-full pl-9 pr-3 py-2.5 bg-[#0b0c10] border border-[#2a2f3a] focus:border-[#ff7a1a] focus:ring-2 focus:ring-[#ff7a1a]/20 rounded-lg text-white outline-none text-sm"
        />
      </div>
      <div className="flex flex-wrap gap-1.5">
        {facets.categories.map((c) => (
          <Chip key={c} active={category === c} onClick={() => setCategory(category === c ? '' : c)}>{c}</Chip>
        ))}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {facets.equipment.map((c) => (
          <Chip key={c} active={equipment === c} onClick={() => setEquipment(equipment === c ? '' : c)}>{c}</Chip>
        ))}
      </div>
      <div className="max-h-[360px] overflow-y-auto rounded-lg border border-[#1f2937] divide-y divide-[#1f2937]">
        {loading ? (
          <div className="p-3 flex items-center gap-2 text-[#aab1bf] text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : items.length === 0 ? (
          <p className="p-3 text-[#7c8597] text-sm italic">No matches.</p>
        ) : (
          items.map((ex) => {
            const used = selectedIds.has(ex.id);
            return (
              <div key={ex.id} className="px-3 py-2 flex items-center justify-between gap-2 bg-[#0b0c10]">
                <div className="min-w-0">
                  <p className="text-white text-sm font-semibold truncate">{ex.name}</p>
                  <p className="text-[#7c8597] text-[11px]">{ex.category} · {(ex.muscles || []).slice(0, 2).join(', ')}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <a
                    href={youtubeVideoUrl(ex.name)}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`picker-watch-demo-${ex.id}`}
                    aria-label={`Watch demo for ${ex.name}`}
                    className="h-7 w-7 grid place-items-center rounded-full text-[#ff7a1a] hover:bg-[#ff7a1a]/10"
                    title="Watch demo on YouTube"
                  >
                    <PlayCircle className="w-4 h-4" />
                  </a>
                  <button
                    onClick={() => !used && onAdd(ex)}
                    disabled={used}
                    className={`px-2.5 py-1 rounded text-xs font-bold border ${used ? 'border-[#1f2937] text-[#7c8597]' : 'border-[#ff7a1a] text-[#ff7a1a] hover:bg-[#ff7a1a]/10'}`}
                  >
                    {used ? 'Added' : <><Plus className="w-3 h-3 inline" /> Add</>}
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

const RoutineBuilder = ({ onCancel, onSaved }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [exercises, setExercises] = useState([]);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const selectedIds = useMemo(() => new Set(exercises.map((e) => e.exercise_id)), [exercises]);

  const add = (ex) => {
    setExercises((cur) => [...cur, {
      exercise_id: ex.id, exercise_name: ex.name,
      target_sets: 3, target_reps: '8-12', target_rir: 2, notes: '',
    }]);
  };
  const remove = (idx) => setExercises((cur) => cur.filter((_, i) => i !== idx));
  const update = (idx, patch) => setExercises((cur) => cur.map((e, i) => i === idx ? { ...e, ...patch } : e));
  const move = (idx, dir) => setExercises((cur) => {
    const j = idx + dir;
    if (j < 0 || j >= cur.length) return cur;
    const arr = [...cur];
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    return arr;
  });

  const save = async () => {
    if (!name.trim()) { toast({ title: 'Name your routine', variant: 'destructive' }); return; }
    if (exercises.length === 0) { toast({ title: 'Add at least one exercise', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      const r = await api.createRoutine({ name, description, exercises });
      toast({ title: 'Routine saved', description: `${r.exercises.length} exercises` });
      onSaved(r);
    } catch (err) {
      toast({ title: 'Could not save', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <Card title="Build a routine" icon={ListPlus} action={(
      <div className="flex gap-2">
        <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>
        <Btn onClick={save} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save</Btn>
      </div>
    )}>
      <div className="grid lg:grid-cols-[1fr_1fr] gap-5">
        <div className="space-y-4">
          <LabeledInput label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Push day · DBs only" />
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="text-[#cbd5e1] font-medium">Description (optional)</span>
            <textarea
              rows={2} value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="Notes for future you"
              className="bg-[#0b0c10] border border-[#2a2f3a] focus:border-[#ff7a1a] focus:ring-2 focus:ring-[#ff7a1a]/20 rounded-lg px-3 py-2.5 text-white outline-none"
            />
          </label>
          <ExercisePicker onAdd={add} selectedIds={selectedIds} />
        </div>

        <div className="space-y-3">
          <p className="text-[10px] uppercase tracking-wider font-bold text-[#7c8597]">Selected ({exercises.length})</p>
          {exercises.length === 0 ? (
            <Empty>Pick exercises from the library on the left.</Empty>
          ) : (
            <ul className="space-y-2">
              {exercises.map((e, idx) => (
                <li key={idx} className="rounded-lg border border-[#1f2937] bg-[#0b0c10] p-3">
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-white font-semibold text-sm flex-1">{idx + 1}. {e.exercise_name}</span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => move(idx, -1)} className="text-[#7c8597] hover:text-white px-1 text-xs">▲</button>
                      <button onClick={() => move(idx, 1)} className="text-[#7c8597] hover:text-white px-1 text-xs">▼</button>
                      <button onClick={() => remove(idx)} className="text-[#7c8597] hover:text-rose-400 ml-1">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    <label className="text-xs">
                      <span className="text-[#7c8597] uppercase tracking-wider text-[10px] font-bold">Sets</span>
                      <input type="number" min="1" max="20" value={e.target_sets} onChange={(ev) => update(idx, { target_sets: Number(ev.target.value) })}
                        className="w-full mt-1 bg-[#0f1115] border border-[#2a2f3a] focus:border-[#ff7a1a] rounded px-2 py-1 text-white text-sm outline-none" />
                    </label>
                    <label className="text-xs">
                      <span className="text-[#7c8597] uppercase tracking-wider text-[10px] font-bold">Reps</span>
                      <input value={e.target_reps} onChange={(ev) => update(idx, { target_reps: ev.target.value })}
                        className="w-full mt-1 bg-[#0f1115] border border-[#2a2f3a] focus:border-[#ff7a1a] rounded px-2 py-1 text-white text-sm outline-none" />
                    </label>
                    <label className="text-xs">
                      <span className="text-[#7c8597] uppercase tracking-wider text-[10px] font-bold">RIR</span>
                      <input type="number" min="0" max="10" value={e.target_rir} onChange={(ev) => update(idx, { target_rir: Number(ev.target.value) })}
                        className="w-full mt-1 bg-[#0f1115] border border-[#2a2f3a] focus:border-[#ff7a1a] rounded px-2 py-1 text-white text-sm outline-none" />
                    </label>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Card>
  );
};

const RoutineDetail = ({ routine, onBack, onDeleted }) => {
  const { toast } = useToast();
  const [logging, setLogging] = useState({});
  const [busy, setBusy] = useState(null);

  const logSet = async (idx) => {
    const ex = routine.exercises[idx];
    const entry = logging[idx] || { weight_kg: 0, sets: 1, reps: 0, rir: ex.target_rir };
    if (!entry.weight_kg || !entry.reps) {
      toast({ title: 'Fill weight and reps', variant: 'destructive' });
      return;
    }
    setBusy(idx);
    try {
      await api.addLift({ exercise: ex.exercise_name, ...entry });
      toast({ title: 'Logged', description: `${ex.exercise_name}: ${entry.weight_kg}kg × ${entry.reps}` });
    } catch (err) {
      toast({ title: 'Failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setBusy(null); }
  };

  const del = async () => {
    if (!window.confirm(`Delete routine "${routine.name}"?`)) return;
    try {
      await api.deleteRoutine(routine.id);
      toast({ title: 'Routine deleted' });
      onDeleted();
    } catch (err) {
      toast({ title: 'Failed', description: err.message, variant: 'destructive' });
    }
  };

  return (
    <Card title={routine.name} icon={Dumbbell} action={(
      <div className="flex gap-2">
        <Btn variant="ghost" onClick={onBack}><ChevronLeft className="w-4 h-4" /> Back</Btn>
        <Btn variant="ghost" onClick={del}><Trash2 className="w-4 h-4" /> Delete</Btn>
      </div>
    )}>
      {routine.description && <p className="text-[#aab1bf] text-sm -mt-2 mb-4">{routine.description}</p>}
      {Array.isArray(routine.avoided) && routine.avoided.length > 0 && (
        <div className="mb-4">
          <SafetyAdaptedPanel items={routine.avoided} testid="routine-safety-adapted-panel" />
        </div>
      )}
      <ul className="space-y-3">
        {routine.exercises.map((ex, idx) => {
          const v = logging[idx] || {};
          const set = (k) => (e) => setLogging({ ...logging, [idx]: { ...v, [k]: Number(e.target.value) } });
          return (
            <li key={idx} className="rounded-lg border border-[#1f2937] bg-[#0b0c10] p-3">
              <div className="flex items-baseline justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-white font-bold text-sm">{idx + 1}. {ex.exercise_name}</span>
                  <a
                    href={youtubeVideoUrl(ex.exercise_name)}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`routine-watch-demo-${idx}`}
                    aria-label={`Watch demo for ${ex.exercise_name}`}
                    className="inline-flex items-center gap-1 text-[11px] font-bold text-[#ff7a1a] hover:text-[#ff8a33] uppercase tracking-wider"
                  >
                    <PlayCircle className="w-3.5 h-3.5" />
                    Demo
                    {hasCuratedVideo(ex.exercise_name) && (
                      <span
                        data-testid={`routine-curated-badge-${idx}`}
                        className="ml-1 px-1.5 py-0.5 rounded-full bg-[#BEFF38] text-black text-[9px] font-black uppercase tracking-wider"
                      >Curated</span>
                    )}
                  </a>
                </div>
                <span className="text-[#ff7a1a] text-xs font-bold">target {ex.target_sets} × {ex.target_reps} @ RIR {ex.target_rir}</span>
              </div>
              <div className="mt-2 grid grid-cols-4 gap-2">
                <input placeholder="kg" type="number" step="0.5" onChange={set('weight_kg')}
                  className="bg-[#0f1115] border border-[#2a2f3a] focus:border-[#ff7a1a] rounded px-2 py-1.5 text-white text-sm outline-none" />
                <input placeholder="sets" type="number" min="1" defaultValue="1" onChange={set('sets')}
                  className="bg-[#0f1115] border border-[#2a2f3a] focus:border-[#ff7a1a] rounded px-2 py-1.5 text-white text-sm outline-none" />
                <input placeholder="reps" type="number" min="1" onChange={set('reps')}
                  className="bg-[#0f1115] border border-[#2a2f3a] focus:border-[#ff7a1a] rounded px-2 py-1.5 text-white text-sm outline-none" />
                <input placeholder="rir" type="number" min="0" max="10" defaultValue={ex.target_rir} onChange={set('rir')}
                  className="bg-[#0f1115] border border-[#2a2f3a] focus:border-[#ff7a1a] rounded px-2 py-1.5 text-white text-sm outline-none" />
              </div>
              <Btn onClick={() => logSet(idx)} disabled={busy === idx} className="mt-2 w-full">
                {busy === idx ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Log set
              </Btn>
              <div className="mt-2">
                <RestTimer defaultSeconds={90} />
              </div>
              <ExerciseHistory exerciseName={ex.exercise_name} />
            </li>
          );
        })}
      </ul>
    </Card>
  );
};

const Routines = () => {
  const [routines, setRoutines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('list'); // 'list' | 'build' | 'detail'
  const [active, setActive] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const { toast } = useToast();

  const refresh = async () => {
    try { setRoutines(await api.listRoutines() || []); }
    catch (err) { toast({ title: 'Load failed', description: err.message, variant: 'destructive' }); }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  if (mode === 'build') {
    return <RoutineBuilder onCancel={() => setMode('list')} onSaved={(r) => { refresh(); setActive(r); setMode('detail'); }} />;
  }
  if (mode === 'detail' && active) {
    return <RoutineDetail routine={active} onBack={() => { setActive(null); setMode('list'); }} onDeleted={() => { setActive(null); setMode('list'); refresh(); }} />;
  }

  return (
    <>
      <Card title="My routines" icon={Dumbbell} action={(
        <div className="flex gap-2">
          <Btn variant="secondary" onClick={() => setAiOpen(true)}><Sparkles className="w-4 h-4" /> AI generate</Btn>
          <Btn onClick={() => setMode('build')}><Plus className="w-4 h-4" /> New routine</Btn>
        </div>
      )}>
        {loading ? (
          <div className="flex items-center gap-2 text-[#aab1bf] text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : routines.length === 0 ? (
          <Empty>No routines yet — build one manually, or tap "AI generate" to have one tailored to your equipment and injuries.</Empty>
        ) : (
          <ul className="grid sm:grid-cols-2 gap-3">
            {routines.map((r) => (
              <li key={r.id}>
                <button onClick={() => { setActive(r); setMode('detail'); }}
                  className="w-full text-left rounded-xl border border-[#1f2937] bg-[#0b0c10] hover:border-[#ff7a1a]/60 p-4 transition-colors">
                  <p className="text-white font-bold">{r.name}</p>
                  <p className="text-[#7c8597] text-xs mt-0.5">{r.exercises.length} exercise{r.exercises.length === 1 ? '' : 's'} · {new Date(r.created_at).toLocaleDateString()}</p>
                  {r.description && <p className="text-[#aab1bf] text-xs mt-2 line-clamp-2">{r.description}</p>}
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>
      {aiOpen && (
        <AIGenerator
          onClose={() => setAiOpen(false)}
          onGenerated={(r) => { setAiOpen(false); refresh(); setActive(r); setMode('detail'); }}
        />
      )}
    </>
  );
};

export default Routines;
